/* bivalta-theme.js */
(() => {
  const STORAGE_KEY = 'bivalta-theme';
  const root = document.documentElement;

  function getStoredTheme() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (_) {
      return null;
    }
  }

  function getDefaultTheme() {
    return root.dataset.defaultTheme === 'dark'
      ? 'dark'
      : 'light';
  }

  function resolveTheme() {
    const storedTheme = getStoredTheme();

    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }

    return getDefaultTheme();
  }

  function applyTheme(theme) {
    root.dataset.theme = theme;
    root.style.colorScheme = theme;

    document.dispatchEvent(
      new CustomEvent('bivalta:theme-change', {
        detail: { theme }
      })
    );
  }

  function setTheme(theme) {
    if (theme !== 'light' && theme !== 'dark') {
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {}

    applyTheme(theme);
  }

  function toggleTheme() {
    const currentTheme =
      root.dataset.theme === 'dark'
        ? 'dark'
        : 'light';

    const nextTheme =
      currentTheme === 'dark'
        ? 'light'
        : 'dark';

    setTheme(nextTheme);
  }

  applyTheme(resolveTheme());

  document.addEventListener('click', (event) => {
    const toggle =
      event.target.closest('[data-bivalta-theme-toggle]');

    if (!toggle) {
      return;
    }

    toggleTheme();
  });

  window.BivaltaTheme = {
    set: setTheme,
    toggle: toggleTheme,
    current: () => root.dataset.theme
  };
})();
;
/* bivalta-header.js */
(() => {
  function initializeBivaltaHeader() {
    const headers =
      document.querySelectorAll('[data-bivalta-header]');

    headers.forEach((header) => {
      if (header.dataset.bivaltaInitialized === 'true') {
        return;
      }

      header.dataset.bivaltaInitialized = 'true';

      initializeSearch(header);
      initializeMobileMenu(header);
      initializeLanguageSelector(header);
      initializeDesktopSubmenus(header);
      initializeMobileSubmenus(header);
    });
  }


  function initializeSearch(header) {
    const toggle =
      header.querySelector('[data-bivalta-search-toggle]');

    const panel =
      header.querySelector('[data-bivalta-search-panel]');

    const close =
      header.querySelector('[data-bivalta-search-close]');

    const input =
      header.querySelector('.bivalta-header__search-input');

    if (!toggle || !panel) {
      return;
    }

    const openSearch = () => {
      panel.hidden = false;

      toggle.setAttribute(
        'aria-expanded',
        'true'
      );

      window.requestAnimationFrame(() => {
        input?.focus();
      });
    };


    const closeSearch = () => {
      panel.hidden = true;

      toggle.setAttribute(
        'aria-expanded',
        'false'
      );
    };


    toggle.addEventListener('click', () => {
      if (panel.hidden) {
        openSearch();
      } else {
        closeSearch();
      }
    });


    close?.addEventListener(
      'click',
      closeSearch
    );


    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          !panel.hidden
        ) {
          closeSearch();
          toggle.focus();
        }
      }
    );
  }


  function initializeMobileMenu(header) {
    const openButton =
      header.querySelector(
        '[data-bivalta-mobile-menu-open]'
      );

    const menu =
      header.querySelector(
        '[data-bivalta-mobile-menu]'
      );

    const drawer =
      menu?.querySelector(
        '.bivalta-mobile-menu__drawer'
      );

    const closeButtons =
      header.querySelectorAll(
        '[data-bivalta-mobile-menu-close]'
      );

    if (!openButton || !menu || !drawer) {
      return;
    }


    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');


    const getFocusableElements = () =>
      Array.from(
        drawer.querySelectorAll(
          focusableSelector
        )
      ).filter((element) =>
        !element.hasAttribute('hidden') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.offsetParent !== null
      );


    const openMenu = () => {
      menu.classList.add('is-open');

      menu.setAttribute(
        'aria-hidden',
        'false'
      );

      openButton.setAttribute(
        'aria-expanded',
        'true'
      );

      document.body.classList.add(
        'bivalta-menu-open'
      );

      const closeButton =
        drawer.querySelector(
          '.bivalta-mobile-menu__header ' +
          '[data-bivalta-mobile-menu-close]'
        );

      window.requestAnimationFrame(() => {
        (closeButton || drawer).focus();
      });
    };


    const closeMenu = ({ restoreFocus = true } = {}) => {
      menu.classList.remove('is-open');

      menu.setAttribute(
        'aria-hidden',
        'true'
      );

      openButton.setAttribute(
        'aria-expanded',
        'false'
      );

      document.body.classList.remove(
        'bivalta-menu-open'
      );

      if (restoreFocus) {
        window.requestAnimationFrame(() => {
          openButton.focus();
        });
      }
    };


    openButton.addEventListener(
      'click',
      openMenu
    );


    closeButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => closeMenu()
      );
    });


    menu.addEventListener(
      'keydown',
      (event) => {
        if (!menu.classList.contains('is-open')) {
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu();
          return;
        }

        if (event.key !== 'Tab') {
          return;
        }

        const focusable =
          getFocusableElements();

        if (focusable.length === 0) {
          event.preventDefault();
          drawer.focus();
          return;
        }

        const first = focusable[0];
        const last =
          focusable[focusable.length - 1];

        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          event.preventDefault();
          last.focus();
          return;
        }

        if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    );
  }


  function initializeLanguageSelector(header) {
    const selectors = header.querySelectorAll('[data-bivalta-language-select]');

    selectors.forEach((selector) => {
      selector.addEventListener('change', () => {
        const form = selector.form;

        if (!form || !selector.value) {
          return;
        }

        form.submit();
      });
    });
  }


  function initializeDesktopSubmenus(header) {
    const toggles =
      header.querySelectorAll(
        '.bivalta-header__submenu-toggle'
      );


    toggles.forEach((toggle) => {
      toggle.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          const expanded =
            toggle.getAttribute(
              'aria-expanded'
            ) === 'true';


          toggles.forEach((otherToggle) => {
            if (otherToggle !== toggle) {
              otherToggle.setAttribute(
                'aria-expanded',
                'false'
              );
            }
          });


          toggle.setAttribute(
            'aria-expanded',
            expanded ? 'false' : 'true'
          );
        }
      );
    });


    document.addEventListener(
      'click',
      (event) => {
        if (
          header.contains(event.target)
        ) {
          return;
        }

        toggles.forEach((toggle) => {
          toggle.setAttribute(
            'aria-expanded',
            'false'
          );
        });
      }
    );
  }


  function initializeMobileSubmenus(header) {
    const toggles =
      header.querySelectorAll(
        '[data-bivalta-mobile-submenu-toggle]'
      );


    toggles.forEach((toggle) => {
      const submenu =
        toggle.nextElementSibling;

      if (!submenu) {
        return;
      }


      toggle.addEventListener(
        'click',
        () => {
          const expanded =
            toggle.getAttribute(
              'aria-expanded'
            ) === 'true';


          toggle.setAttribute(
            'aria-expanded',
            expanded ? 'false' : 'true'
          );


          submenu.hidden =
            expanded;
        }
      );
    });
  }


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeBivaltaHeader
    );
  } else {
    initializeBivaltaHeader();
  }


  document.addEventListener(
    'shopify:section:load',
    initializeBivaltaHeader
  );
})();
;
/* bivalta-home.js */
(() => {
  const generalI18n =
    window.BivaltaI18n?.general || {};

  function initializeDiscountButtons(root = document) {
    const buttons =
      root.querySelectorAll(
        '[data-bivalta-copy-code]'
      );

    buttons.forEach((button) => {
      if (
        button.dataset.bivaltaInitialized ===
        'true'
      ) {
        return;
      }

      button.dataset.bivaltaInitialized =
        'true';


      button.addEventListener(
        'click',
        async () => {
          const code =
            button.dataset.code;

          if (!code) {
            return;
          }


          const originalText =
            button.textContent.trim();


          try {
            await navigator.clipboard.writeText(
              code
            );

            button.textContent =
              generalI18n.copied ||
              originalText;

            button.classList.add(
              'is-success'
            );


            window.setTimeout(
              () => {
                button.textContent =
                  originalText;

                button.classList.remove(
                  'is-success'
                );
              },
              1800
            );

          } catch (_) {

            const textarea =
              document.createElement(
                'textarea'
              );

            textarea.value =
              code;

            textarea.style.position =
              'fixed';

            textarea.style.opacity =
              '0';

            document.body.appendChild(
              textarea
            );

            textarea.select();

            document.execCommand(
              'copy'
            );

            textarea.remove();


            button.textContent =
              generalI18n.copied ||
              originalText;

            button.classList.add(
              'is-success'
            );


            window.setTimeout(
              () => {
                button.textContent =
                  originalText;

                button.classList.remove(
                  'is-success'
                );
              },
              1800
            );
          }
        }
      );
    });
  }


  /* =======================================================
     PRODUCT CAROUSELS
     ======================================================= */

  function initializeProductCarousels(
    root = document
  ) {
    const sections =
      root.querySelectorAll(
        '[data-bivalta-product-carousel]'
      );


    sections.forEach((section) => {
      if (
        section.dataset
          .bivaltaCarouselInitialized ===
        'true'
      ) {
        return;
      }


      section.dataset
        .bivaltaCarouselInitialized =
        'true';


      const track =
        section.querySelector(
          '[data-bivalta-product-carousel-track]'
        );


      const previousButton =
        section.querySelector(
          '[data-bivalta-product-carousel-prev]'
        );


      const nextButton =
        section.querySelector(
          '[data-bivalta-product-carousel-next]'
        );


      if (!track) {
        return;
      }


      const prefersReducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        );

      const getScrollBehavior =
        () =>
          prefersReducedMotion.matches
            ? 'auto'
            : 'smooth';


      /*
       * Calculate one slide width + carousel gap.
       * This keeps the arrows working on desktop,
       * tablet and mobile without hardcoded pixels.
       */

      const getScrollStep = () => {
        const slide =
          track.querySelector(
            '.bivalta-products-carousel__slide'
          );


        if (!slide) {
          return track.clientWidth;
        }


        const trackStyles =
          window.getComputedStyle(
            track
          );


        const gap =
          Number.parseFloat(
            trackStyles.columnGap ||
            trackStyles.gap ||
            '0'
          ) || 0;


        return (
          slide.getBoundingClientRect()
            .width +
          gap
        );
      };


      /* Previous */

      previousButton?.addEventListener(
        'click',
        () => {
          track.scrollBy({
            left:
              -getScrollStep(),

            behavior:
              getScrollBehavior()
          });
        }
      );


      /* Next */

      nextButton?.addEventListener(
        'click',
        () => {
          track.scrollBy({
            left:
              getScrollStep(),

            behavior:
              getScrollBehavior()
          });
        }
      );


      /*
       * Mouse drag support.
       * Native touch scrolling continues to work
       * automatically on mobile/tablet.
       */

      let isDragging =
        false;

      let dragStartX =
        0;

      let dragStartScrollLeft =
        0;


      track.addEventListener(
        'mousedown',
        (event) => {
          /*
           * Do not start dragging when clicking
           * interactive controls inside a product.
           */

          if (
            event.target.closest(
              'a, button, input, select, textarea'
            )
          ) {
            return;
          }


          isDragging =
            true;

          dragStartX =
            event.pageX;

          dragStartScrollLeft =
            track.scrollLeft;

          track.classList.add(
            'is-dragging'
          );
        }
      );


      track.addEventListener(
        'mousemove',
        (event) => {
          if (!isDragging) {
            return;
          }


          event.preventDefault();


          const distance =
            event.pageX -
            dragStartX;


          track.scrollLeft =
            dragStartScrollLeft -
            distance;
        }
      );


      const stopDragging = () => {
        if (!isDragging) {
          return;
        }


        isDragging =
          false;

        track.classList.remove(
          'is-dragging'
        );
      };


      track.addEventListener(
        'mouseup',
        stopDragging
      );


      track.addEventListener(
        'mouseleave',
        stopDragging
      );


      /*
       * Keyboard accessibility.
       */

      track.setAttribute(
        'tabindex',
        '0'
      );


      track.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key ===
            'ArrowLeft'
          ) {
            event.preventDefault();

            track.scrollBy({
              left:
                -getScrollStep(),

              behavior:
                getScrollBehavior()
            });
          }


          if (
            event.key ===
            'ArrowRight'
          ) {
            event.preventDefault();

            track.scrollBy({
              left:
                getScrollStep(),

              behavior:
                getScrollBehavior()
            });
          }
        }
      );


      /*
       * Disable arrows at start/end.
       */

      const updateButtons = () => {
        const maxScroll =
          Math.max(
            0,
            track.scrollWidth -
            track.clientWidth
          );


        const isAtStart =
          track.scrollLeft <=
          3;


        const isAtEnd =
          track.scrollLeft >=
          maxScroll - 3;


        if (previousButton) {
          previousButton.disabled =
            isAtStart;

          previousButton.classList.toggle(
            'is-disabled',
            isAtStart
          );
        }


        if (nextButton) {
          nextButton.disabled =
            isAtEnd;

          nextButton.classList.toggle(
            'is-disabled',
            isAtEnd
          );
        }
      };


      let scrollTicking =
        false;


      track.addEventListener(
        'scroll',
        () => {
          if (scrollTicking) {
            return;
          }


          scrollTicking =
            true;


          window.requestAnimationFrame(
            () => {
              updateButtons();

              scrollTicking =
                false;
            }
          );
        },
        {
          passive: true
        }
      );


      window.addEventListener(
        'resize',
        updateButtons
      );


      updateButtons();
    });
  }


  /* =======================================================
     INITIALIZE HOMEPAGE
     ======================================================= */

  function initializeHome(
    root = document
  ) {
    initializeDiscountButtons(
      root
    );

    initializeProductCarousels(
      root
    );
  }


  /* =======================================================
     DOM READY
     ======================================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeHome();
      }
    );

  } else {
    initializeHome();
  }


  /* =======================================================
     SHOPIFY THEME EDITOR
     ======================================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeHome(
        event.target
      );
    }
  );
})();